/**
 * The small amount of machinery every mini game needs.
 *
 * Twenty games written from scratch would each grow their own loop, their own
 * teardown and their own way of forgetting to cancel a timer. `Lifecycle` makes
 * cleanup the default: everything you start through it is registered, and one
 * call stops the lot. That is the difference between a shell that can host
 * twenty games and one that slowly fills up with ghosts of the last three.
 */

export class Lifecycle {
  private readonly stops: Array<() => void> = [];
  private stopped = false;

  /** setInterval that cannot outlive the game. */
  interval(fn: () => void, ms: number): void {
    const id = window.setInterval(fn, ms);
    this.stops.push(() => window.clearInterval(id));
  }

  /** setTimeout that cannot outlive the game. */
  timeout(fn: () => void, ms: number): number {
    const id = window.setTimeout(fn, ms);
    this.stops.push(() => window.clearTimeout(id));
    return id;
  }

  /** addEventListener that cannot outlive the game. */
  on<K extends keyof WindowEventMap>(
    target: Window,
    type: K,
    fn: (ev: WindowEventMap[K]) => void,
    opts?: AddEventListenerOptions,
  ): void;
  on(
    target: EventTarget,
    type: string,
    fn: (ev: Event) => void,
    opts?: AddEventListenerOptions,
  ): void;
  on(
    target: EventTarget,
    type: string,
    fn: (ev: never) => void,
    opts?: AddEventListenerOptions,
  ): void {
    const handler = fn as EventListener;
    target.addEventListener(type, handler, opts);
    this.stops.push(() => target.removeEventListener(type, handler, opts));
  }

  /**
   * A requestAnimationFrame loop, given the delta in seconds.
   *
   * The delta is clamped: a backgrounded tab returns with a gap of many seconds,
   * and a physics step that large teleports things through walls.
   */
  frame(fn: (dt: number) => void): void {
    let raf = 0;
    let last = performance.now();
    const tick = (now: number): void => {
      if (this.stopped) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      fn(dt);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    this.stops.push(() => cancelAnimationFrame(raf));
  }

  /** Anything else that needs undoing. */
  add(stop: () => void): void {
    this.stops.push(stop);
  }

  stop(): void {
    this.stopped = true;
    for (const s of this.stops.splice(0).reverse()) {
      try {
        s();
      } catch {
        // One broken teardown must not strand the others.
      }
    }
  }
}

/** Shorthand element builder. Keeps the games readable rather than DOM soup. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

/**
 * A canvas that fills its box and stays sharp on high density screens.
 *
 * Returns logical dimensions, never device pixels: every game then works in CSS
 * pixels and the scaling is somebody else's problem. Getting this wrong is what
 * made the arena render at half size on phones, so it lives in one place here.
 */
export function fitCanvas(
  canvas: HTMLCanvasElement,
  life: Lifecycle,
  onResize?: (w: number, h: number) => void,
): { w: () => number; h: () => number; ctx: CanvasRenderingContext2D } {
  const ctx = canvas.getContext('2d')!;
  let w = 1;
  let h = 1;

  const resize = (): void => {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    w = Math.max(1, Math.round(rect.width));
    h = Math.max(1, Math.round(rect.height));
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    onResize?.(w, h);
  };

  resize();
  life.on(window, 'resize', resize);
  // The box can change without the window changing, for instance when the rules
  // panel wraps to another line on rotation.
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    life.add(() => ro.disconnect());
  }

  return { w: () => w, h: () => h, ctx };
}

/** A short synthesised note. Same approach as the arena: no assets to fail. */
let audio: AudioContext | null = null;
export function tone(freq: number, ms = 90, type: OscillatorType = 'sine', gain = 0.05): void {
  try {
    audio ??= new (window.AudioContext ??
      (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (audio.state === 'suspended') void audio.resume();
    const osc = audio.createOscillator();
    const vol = audio.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    vol.gain.setValueAtTime(gain, audio.currentTime);
    vol.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + ms / 1000);
    osc.connect(vol).connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + ms / 1000);
  } catch {
    // Sound is a nicety. Never let it break a game.
  }
}

/**
 * A seedless shuffle. Fisher Yates, in place, returning the same array.
 *
 * Written out rather than `sort(() => Math.random() - 0.5)`, which is not a
 * shuffle: it is biased badly enough to be visible over twenty rounds of a
 * memory game.
 */
export function shuffle<T>(items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

export function randomOf<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/** Format seconds as m:ss, for the several games that show a clock. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * An on-screen direction pad, for the games that are otherwise swipe only.
 *
 * Added because 2048 was close to unplayable on a tablet. A swipe has to be
 * distinguished from a scroll, a drag and a stray tap, and on a touch screen
 * that guess is wrong often enough that a game which only accepts swipes feels
 * broken rather than fiddly. Four buttons are never ambiguous.
 *
 * The swipe still works and the keyboard still works. This is a third way in,
 * not a replacement, because a mouse user does not want a d-pad and a keyboard
 * user never looks at it.
 *
 * `press` is called with a unit direction. Repeats are the caller's business:
 * a grid puzzle wants one move per tap, and a snake wants a held turn.
 */
export function dpad(life: Lifecycle, press: (dx: number, dy: number) => void): HTMLElement {
  const wrap = h('div', 'ac-dpad');
  const make = (label: string, dx: number, dy: number, area: string): HTMLElement => {
    const b = h('button', 'ac-dkey', label);
    b.style.gridArea = area;
    b.type = 'button';
    // pointerdown, not click: a tap should register the moment it lands rather
    // than waiting for the release, which is what makes a d-pad feel direct.
    // preventDefault stops the browser turning the same tap into a scroll or a
    // double tap zoom, which is the whole problem this exists to solve.
    life.on(b, 'pointerdown', (ev: Event) => {
      ev.preventDefault();
      press(dx, dy);
    });
    // Without this the browser still fires a synthetic click, and a fast player
    // gets two moves for one tap.
    life.on(b, 'click', (ev: Event) => ev.preventDefault());
    return b;
  };
  wrap.append(
    make('\u2191', 0, -1, 'up'),
    make('\u2190', -1, 0, 'left'),
    make('\u2193', 0, 1, 'down'),
    make('\u2192', 1, 0, 'right'),
  );
  return wrap;
}
